import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Link } from '@tiptap/extension-link';
import { Image } from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { TextAlign } from '@tiptap/extension-text-align';
import { Highlight } from '@tiptap/extension-highlight';
import { Underline } from '@tiptap/extension-underline';
import { Superscript } from '@tiptap/extension-superscript';
import { useRef, useCallback, useEffect } from 'react';
import api from '../lib/api';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  Heading1, Heading2, Heading3,
  List, ListOrdered,
  AlignRight, AlignCenter, AlignLeft,
  Highlighter, Link as LinkIcon, Image as ImageIcon,
  Table as TableIcon, Undo2, Redo2, Quote, Minus,
} from 'lucide-react';
import { toast } from 'sonner';

const MenuButton = ({ onClick, active, disabled, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`p-1.5 rounded transition-colors ${
      active ? 'bg-primary/20 text-primary' : 'text-gray-600 hover:bg-gray-100'
    } ${disabled ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    {children}
  </button>
);

const RichTextEditor = ({ value, onChange, onEditorReady, extraToolbar }) => {
  const fileRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-primary underline cursor-pointer' } }),
      Image.configure({ inline: false, allowBase64: true }),
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Highlight.configure({ multicolor: true }),
      Underline,
      Superscript,
    ],
    content: value || '',
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    onSelectionUpdate: ({ editor }) => {
      // If cursor is NOT inside a sup element, ensure stored marks don't include superscript.
      // This fixes: typing right after a <sup>[N]</sup> shouldn't continue as superscript.
      try {
        const { from } = editor.state.selection;
        const $pos = editor.state.doc.resolve(from);
        const marksAtPos = $pos.marks();
        const supType = editor.schema.marks.superscript;
        if (!supType) return;
        const hasSup = marksAtPos.some(m => m.type === supType);
        const stored = editor.state.storedMarks;
        const storedHasSup = stored && stored.some(m => m.type === supType);
        if (!hasSup && storedHasSup) {
          editor.view.dispatch(editor.state.tr.setStoredMarks(stored.filter(m => m.type !== supType)));
        }
      } catch {}
    },
    editorProps: {
      attributes: { class: 'prose prose-lg max-w-none focus:outline-none min-h-[300px] p-4 text-right', dir: 'rtl' },
    },
  });

  useEffect(() => {
    if (editor && onEditorReady) onEditorReady(editor);
  }, [editor, onEditorReady]);

  useEffect(() => {
    if (editor && value && editor.getHTML() !== value && !editor.isFocused) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  const handleImageUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/upload/image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${res.data.path}`;
      editor?.chain().focus().setImage({ src: url }).run();
      toast.success('تم رفع الصورة');
    } catch {
      toast.error('فشل رفع الصورة');
    }
    if (fileRef.current) fileRef.current.value = '';
  }, [editor]);

  const addLink = useCallback(() => {
    const url = window.prompt('أدخل الرابط:');
    if (!url) return;
    editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const addTable = useCallback(() => {
    editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  }, [editor]);

  if (!editor) return null;

  const s = 16;

  return (
    <div className="border-2 border-gray-200 rounded-xl overflow-hidden bg-white">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 p-2 border-b bg-gray-50/80 sticky top-0 z-10" dir="ltr">
        <MenuButton onClick={() => editor.chain().focus().undo().run()} title="تراجع"><Undo2 size={s} /></MenuButton>
        <MenuButton onClick={() => editor.chain().focus().redo().run()} title="إعادة"><Redo2 size={s} /></MenuButton>
        <div className="w-px h-6 bg-gray-200 mx-1" />

        <MenuButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="عريض"><Bold size={s} /></MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="مائل"><Italic size={s} /></MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="تحته خط"><UnderlineIcon size={s} /></MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="يتوسطه خط"><Strikethrough size={s} /></MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleHighlight().run()} active={editor.isActive('highlight')} title="تظليل"><Highlighter size={s} /></MenuButton>
        <div className="w-px h-6 bg-gray-200 mx-1" />

        <MenuButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="عنوان 1"><Heading1 size={s} /></MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="عنوان 2"><Heading2 size={s} /></MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="عنوان 3"><Heading3 size={s} /></MenuButton>
        <div className="w-px h-6 bg-gray-200 mx-1" />

        <MenuButton onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="يمين"><AlignRight size={s} /></MenuButton>
        <MenuButton onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="وسط"><AlignCenter size={s} /></MenuButton>
        <MenuButton onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="يسار"><AlignLeft size={s} /></MenuButton>
        <div className="w-px h-6 bg-gray-200 mx-1" />

        <MenuButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="قائمة"><List size={s} /></MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="قائمة مرقمة"><ListOrdered size={s} /></MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="اقتباس"><Quote size={s} /></MenuButton>
        <MenuButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title="خط فاصل"><Minus size={s} /></MenuButton>
        <div className="w-px h-6 bg-gray-200 mx-1" />

        <MenuButton onClick={addLink} active={editor.isActive('link')} title="رابط"><LinkIcon size={s} /></MenuButton>
        <MenuButton onClick={() => fileRef.current?.click()} title="صورة"><ImageIcon size={s} /></MenuButton>
        <MenuButton onClick={addTable} title="جدول"><TableIcon size={s} /></MenuButton>
        {extraToolbar && (
          <>
            <div className="w-px h-6 bg-gray-200 mx-1" />
            {extraToolbar(editor, MenuButton)}
          </>
        )}
      </div>

      {/* Editor */}
      <EditorContent editor={editor} />
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />

      <style>{`
        .ProseMirror { direction: rtl; text-align: right; }
        .ProseMirror h1 { font-size: 1.75em; font-weight: 700; margin: 0.8em 0 0.4em; }
        .ProseMirror h2 { font-size: 1.4em; font-weight: 700; margin: 0.7em 0 0.3em; }
        .ProseMirror h3 { font-size: 1.2em; font-weight: 600; margin: 0.6em 0 0.3em; }
        .ProseMirror p { margin: 0.5em 0; line-height: 1.8; }
        .ProseMirror ul, .ProseMirror ol { padding-right: 1.5em; margin: 0.5em 0; }
        .ProseMirror li { margin: 0.2em 0; }
        .ProseMirror blockquote { border-right: 4px solid #C19A6B; padding-right: 1em; margin: 1em 0; color: #666; font-style: italic; }
        .ProseMirror a { color: #C19A6B; text-decoration: underline; cursor: pointer; }
        .ProseMirror img { max-width: 100%; height: auto; border-radius: 8px; margin: 1em 0; }
        .ProseMirror table { border-collapse: collapse; width: 100%; margin: 1em 0; }
        .ProseMirror th, .ProseMirror td { border: 1px solid #ddd; padding: 8px 12px; text-align: right; }
        .ProseMirror th { background: #f5f5f5; font-weight: 600; }
        .ProseMirror mark { background: #fef08a; padding: 0 2px; border-radius: 2px; }
        .ProseMirror hr { border: none; border-top: 2px solid #e5e5e5; margin: 1.5em 0; }
        .ProseMirror sup { color: #C19A6B; font-weight: 700; cursor: pointer; padding: 0 2px; background: #fff7ed; border-radius: 3px; font-size: 0.75em; vertical-align: super; line-height: 0; margin: 0 1px; }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
